import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const catArticles = articles
    .filter((a) => a.categorySlug === slug)
    .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-2xl mb-4">
            <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
          <p className="text-foreground-500 mb-6">你访问的分类不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `https://chiguawang.com/category/${slug}/#collectionpage`,
        url: `https://chiguawang.com/category/${slug}`,
        name: `${category.name} - 吃瓜网`,
        description: category.description,
        isPartOf: { '@id': 'https://chiguawang.com/#website' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chiguawang.com' },
          { '@type': 'ListItem', position: 2, name: category.name, item: `https://chiguawang.com/category/${slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Breadcrumb */}
        <div className="pt-20 pb-0">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 py-3">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900 font-medium">{category.name}</span>
              </nav>
            </div>
          </div>
        </div>

        {/* Header */}
        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center bg-accent-100 rounded-2xl">
                <i className={`${category.icon} text-accent-600 text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">{category.description}</p>
              </div>
            </div>
            <p className="text-sm text-foreground-400 mt-2">
              共 <strong className="text-foreground-700">{catArticles.length}</strong> 篇相关文章
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-7xl mx-auto">
            {catArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-2xl mb-4">
                  <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {catArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-300/60 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      {article.isHot && (
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                            <i className="ri-fire-line mr-1"></i>热门
                          </span>
                        </div>
                      )}
                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-950/60 backdrop-blur-sm text-background-50">
                          <i className="ri-eye-line mr-1"></i>
                          {formatViews(article.views)}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h2 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.author}</span>
                        <span>{article.publishDate}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}